import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Device } from '../../types';
import { otaModeColorStyle, otaModeLabel, resolveOtaMode } from '../../utils/otaMode';
import { API_BASE_URL } from '../../config/api';
import SentryDropdown, { sentryContainsQuery } from '../SentryDropdown';

const InternalDevicesTab: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDevDevices = useCallback(async () => {
    setLoadingDevices(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/devices`, {
        withCredentials: true,
        params: { type: 'dev', limit: 100, page: 1, sortColumn: 'serial' },
      });
      setDevices(response.data.devices || []);
      setDevicesTotal(response.data.totalDevices || 0);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching internal devices:', err);
      setError(err.response?.data?.message || 'Failed to load internal devices');
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    fetchDevDevices();
  }, [fetchDevDevices]);

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">Internal Devices</h4>
        <span className="text-muted small">{devicesTotal.toLocaleString()} total</span>
      </div>

      {loadingDevices ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-sm align-middle">
            <thead>
              <tr>
                <th>Serial</th>
                <th>Model</th>
                <th>Firmware</th>
                <th>OTA</th>
                <th>Owner</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {devices.length > 0 ? (
                devices.map((d) => (
                  <tr key={d._id}>
                    <td>
                      <code>{d.serial}</code>
                    </td>
                    <td>{d.model}</td>
                    <td>{d.firmware}</td>
                    <td>
                      <span className="badge" style={otaModeColorStyle(resolveOtaMode(d.deployment))}>
                        {otaModeLabel(resolveOtaMode(d.deployment))}
                      </span>
                    </td>
                    <td className="text-truncate" style={{ maxWidth: 160 }}>
                      {d.user
                        ? `${d.user.firstName} ${d.user.lastName}`
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-end text-nowrap">
                      <SentryDropdown
                        issuesQuery={sentryContainsQuery('serial', d.serial)}
                        logsFields={['timestamp', 'message', 'serial']}
                      />
                      <Link
                        className="btn btn-outline-primary btn-sm"
                        to={`/device/${d.serial}`}
                        title="View device"
                      >
                        View <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-1" />
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No devices with deployment type dev
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default InternalDevicesTab;
